import { Button, Flex, Spacing } from '@billy/design-system'
import { ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import { COLLAPSE_ANIMATION_MS } from './constants/collapseAnimationMs'
import * as Styled from './styles'

interface ExpandableSectionProps {
  children: ReactNode
  collapsedTags?: ReactNode
  emptyLabel?: string
  hasValue: boolean
  onToggle?: (isExpanded: boolean) => void
  toggleLabel: string
}

export const ExpandableSection = ({
  children,
  collapsedTags,
  emptyLabel,
  hasValue,
  onToggle,
  toggleLabel,
}: ExpandableSectionProps): ReactElement => {
  const [isSectionExpanded, setIsSectionExpanded] = useState(false)
  const [isSectionFullyClosed, setIsSectionFullyClosed] = useState(true)

  const shouldShowTags = !isSectionExpanded && isSectionFullyClosed && hasValue
  const buttonLabel = emptyLabel && !hasValue && !isSectionExpanded ? emptyLabel : toggleLabel

  const toggleIcon = useMemo(() => {
    if (isSectionExpanded) {
      return 'chevronUp'
    }

    if (hasValue) {
      return 'chevronRight'
    }

    return 'plus'
  }, [isSectionExpanded, hasValue])

  const handleToggle = useCallback(() => {
    const nextIsSectionExpanded = !isSectionExpanded
    setIsSectionExpanded(nextIsSectionExpanded)
    onToggle?.(nextIsSectionExpanded)
  }, [isSectionExpanded, onToggle])

  useEffect(() => {
    if (!isSectionExpanded) {
      const timer = setTimeout(() => setIsSectionFullyClosed(true), COLLAPSE_ANIMATION_MS)

      return () => clearTimeout(timer)
    }

    setIsSectionFullyClosed(false)
  }, [isSectionExpanded])

  return (
    <Flex flexDirection="column">
      <Styled.ExpandableWrapper isVisible={isSectionExpanded} isFullyClosed={isSectionFullyClosed}>
        <Styled.ExpandableContent>{children}</Styled.ExpandableContent>
      </Styled.ExpandableWrapper>

      <Flex alignItems="center" gap={Spacing.S8}>
        <Button aria-expanded={isSectionExpanded} icon={toggleIcon} onClick={handleToggle} size="md" variant="text">
          {buttonLabel}
        </Button>
        {shouldShowTags && <Styled.TagsWrapper>{collapsedTags}</Styled.TagsWrapper>}
      </Flex>
    </Flex>
  )
}
